import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { serviceAPI, hospitalAPI, anesthesiaTypesAPI } from '../services/api';
import Layout from '../components/Layout';

const NewService = () => {
  const [formData, setFormData] = useState({
    hospital_id: '',
    patient_name: '',
    patient_number: '',
    surgery_name: '',
    service_date: '',
    days_of_service: 1,
    amount_charged: '',
    anesthesia_type: '',
    medication_used: ''
  });
  const [file, setFile] = useState(null);
  const [hospitals, setHospitals] = useState([]);
  const [anesthesiaTypes, setAnesthesiaTypes] = useState([]);
  const [newHospitalName, setNewHospitalName] = useState('');
  const [newHospitalDescription, setNewHospitalDescription] = useState('');
  const [showNewHospital, setShowNewHospital] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchHospitals();
    fetchAnesthesiaTypes();
  }, []);

  const fetchHospitals = async () => {
    try {
      const data = await hospitalAPI.getAll();
      setHospitals(data);
    } catch (error) {
      console.error('Error fetching hospitals:', error);
    }
  };

  const fetchAnesthesiaTypes = async () => {
    try {
      const data = await anesthesiaTypesAPI.getAll();
      setAnesthesiaTypes(data);
      console.log('Anesthesia types loaded:', data);
    } catch (error) {
      console.error('Error fetching anesthesia types:', error);
      // Fallback to default types if API fails
      setAnesthesiaTypes([
        { id: 'default-1', name: 'General Anesthesia', type: 'default' },
        { id: 'default-2', name: 'Spinal Anesthesia', type: 'default' },
        { id: 'default-3', name: 'Local Anesthesia', type: 'default' },
        { id: 'default-4', name: 'Epidural Anesthesia', type: 'default' },
        { id: 'default-5', name: 'Regional Anesthesia', type: 'default' },
        { id: 'default-6', name: 'Sedation', type: 'default' }
      ]);
    }
  };

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'number' ? parseFloat(value) || '' : value
    });
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleAddHospital = async () => {
    if (!newHospitalName.trim()) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/hospitals`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: newHospitalName.trim(),
          description: newHospitalDescription.trim()
        })
      });

      if (response.ok) {
        const newHospital = await response.json();
        setHospitals([...hospitals, newHospital]);
        setFormData({ ...formData, hospital_id: newHospital.id });
        setNewHospitalName('');
        setNewHospitalDescription('');
        setShowNewHospital(false);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to add hospital');
      }
    } catch (error) {
      setError('Failed to add hospital');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await serviceAPI.create(formData, file);
      navigate('/dashboard');
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to create service');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-2 sm:px-0">
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">New Service Entry</h1>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 sm:px-4 sm:py-3 rounded-lg mb-4 sm:mb-6 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
            {/* Hospital Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Hospital *
              </label>
              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
                <select
                  name="hospital_id"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  value={formData.hospital_id}
                  onChange={handleChange}
                >
                  <option value="">Select Hospital</option>
                  {hospitals.map((hospital) => (
                    <option key={hospital.id} value={hospital.id}>
                      {hospital.name}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setShowNewHospital(!showNewHospital)}
                  className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 text-sm font-medium"
                >
                  Add New
                </button>
              </div>

              {showNewHospital && (
                <div className="mt-3 space-y-3 p-3 bg-gray-50 rounded-lg">
                  <input
                    type="text"
                    placeholder="Hospital name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={newHospitalName}
                    onChange={(e) => setNewHospitalName(e.target.value)}
                  />
                  <textarea
                    placeholder="Hospital description (optional, max 250 characters)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    rows="2"
                    maxLength="250"
                    value={newHospitalDescription}
                    onChange={(e) => setNewHospitalDescription(e.target.value)}
                  />
                  <div className="text-sm text-gray-500">
                    {newHospitalDescription.length}/250 characters
                  </div>
                  <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
                    <button
                      type="button"
                      onClick={handleAddHospital}
                      className="w-full sm:w-auto bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 text-sm font-medium"
                    >
                      Add Hospital
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowNewHospital(false);
                        setNewHospitalName('');
                        setNewHospitalDescription('');
                      }}
                      className="w-full sm:w-auto border border-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 text-sm font-medium"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Patient Information */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Patient Name *
                </label>
                <input
                  type="text"
                  name="patient_name"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Patient Name"
                  value={formData.patient_name}
                  onChange={handleChange}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Patient Number *
                </label>
                <input
                  type="text"
                  name="patient_number"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="ID/Phone/Unique No."
                  value={formData.patient_number}
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* Surgery Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Name of Surgery *
              </label>
              <input
                type="text"
                name="surgery_name"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., Appendectomy, Cesarean Section, etc."
                value={formData.surgery_name}
                onChange={handleChange}
              />
            </div>

            {/* Service Details */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Service Date *
                </label>
                <input
                  type="date"
                  name="service_date"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  value={formData.service_date}
                  onChange={handleChange}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Days of Service *
                </label>
                <input
                  type="number"
                  name="days_of_service"
                  required
                  min="1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  value={formData.days_of_service}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Amount Charged *
                </label>
                <input
                  type="number"
                  name="amount_charged"
                  required
                  min="0"
                  step="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0.00"
                  value={formData.amount_charged}
                  onChange={handleChange}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type of Anesthesia *
                </label>
                <select
                  name="anesthesia_type"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  value={formData.anesthesia_type}
                  onChange={handleChange}
                >
                  <option value="">Select Type</option>
                  {anesthesiaTypes.map((type) => (
                    <option key={type.id} value={type.name}>
                      {type.name} {type.type === 'custom' && '(Custom)'}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Medication */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Medication Used
              </label>
              <textarea
                name="medication_used"
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="List medications used (optional)"
                value={formData.medication_used}
                onChange={handleChange}
              />
            </div>

            {/* File Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload Bill (PDF/Image)
              </label>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleFileChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              {file && (
                <p className="mt-2 text-sm text-gray-600">
                  Selected: {file.name}
                </p>
              )}
            </div>

            {/* Submit Button */}
            <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3 pt-4">
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="w-full sm:w-auto border border-gray-300 text-gray-700 px-6 py-2 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="w-full sm:w-auto bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {loading ? 'Creating...' : 'Create Service'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default NewService;